import LineItem from "../../../common/LineItem";
import {
  Board,
  BoardContext,
  boardHasBeenPaidFor,
  LeadTimeOption,
  QuoteState,
  Shipment,
} from "../../../../reducers/uploadedBoard";
import {
  dateStringToHumanReadableDate,
  leadTimeInfoString,
} from "../../../../utils/valueFormatting";
import { useBoardFromContext } from "../../../../utils/hooks/commonly-stored/uploaded-board/board";
import Checkbox from "../../../common/Checkbox";
import React, { ReactNode, useState } from "react";
import { TextArea } from "../../../../constants/Styles";
import { SingleValue } from "react-select";
import {
  Label,
  DetailLine,
  NumBoardsContainer,
  QuoteOptionContainer,
  ToolTipWrapper,
} from "../ProjectDetailsStyles";
import { DEMO_BOARD_SLUG } from "../../../../constants/boards";
import { useUpdateCustomerSpecifications } from "../../../../utils/hooks/commonly-stored/uploaded-board/updates/customerSpecifications";
import { useUpdateShipments } from "../../../../utils/hooks/commonly-stored/uploaded-board/updates/shipments";
import FlashPCBSelect from "../../../common/FlashPCBSelect";
import { LineItemDescription } from "../../../common/LineItem/LineItemStyles";
import LoadingOverlay from "../../../LoadingOverlay";
import FlashPCBTooltip from "../../../common/FlashPCBTooltip";
import InfoIcon from "../../../common/FlashPCBTooltip/InfoIcon";
import useShipmentsFromContext from "../../../../utils/hooks/commonly-stored/uploaded-board/shipments";
import { EditButton } from "../../BillOfMaterials/ElementsTable/ElementsTableStyles";
import Icon from "../../../common/Icon";
import { useAppSelector } from "../../../../utils/hooks/store";
import { ProcessingPill } from "../index";
import AddOrEditShipment from "../../Quote/Shipments/AddOrEditShipment";
import Modal from "../../../common/Modal";
import ModalContentWithCloseButton from "../../../common/ModalContentWithCloseButton";

interface LeadTimeSelectOption {
  value: LeadTimeOption;
  label: ReactNode;
  disabled: boolean;
}

function useGetLeadTimeValue() {
  const board = useBoardFromContext().board as Board;
  const shipments = useShipmentsFromContext(board.id) as Shipment[];
  const firstShipment = shipments[0]!;

  return () => {
    const matchingExactOption = firstShipment!.lead_time_options.options.find(
      (option) => {
        return (
          option.base_days == firstShipment!.base_lead_time_days &&
          option.ship_date == firstShipment!.ship_date
        );
      },
    );
    if (matchingExactOption) {
      return {
        value: matchingExactOption,
        label: leadTimeInfoString(
          matchingExactOption.days,
          matchingExactOption.ship_date,
        ),
        disabled: false,
      };
    }

    const matchingOption = firstShipment!.lead_time_options.options.find(
      (option) => {
        return option.base_days == firstShipment!.base_lead_time_days;
      },
    ) as LeadTimeOption;

    const getLabel = () => {
      if (firstShipment!.ship_date && firstShipment!.lead_time_days) {
        return leadTimeInfoString(
          firstShipment!.lead_time_days,
          firstShipment!.ship_date,
        );
      }
      if (
        firstShipment!.ship_date &&
        matchingOption.ship_date != firstShipment!.ship_date
      ) {
        return dateStringToHumanReadableDate(firstShipment!.ship_date);
      }
      return leadTimeInfoString(matchingOption.days, matchingOption.ship_date);
    };

    return {
      value: matchingOption,
      label: getLabel(),
      disabled: false,
    };
  };
}

export default function QuoteDetails() {
  const board = useBoardFromContext().board as Board;
  const {
    updateXrayInspection,
    updateCleaning,
    updateNotes,
    updateConformalCoating,
    updateIncludeDateCode,
  } = useUpdateCustomerSpecifications();
  const { updateShipDate, updatingShipDate, updatingShipment } =
    useUpdateShipments();
  const shipments = useShipmentsFromContext(board.id) as Shipment[];
  const firstShipment = shipments[0]!;
  const [editingNumBoards, setEditingNumBoards] = useState(false);
  const isDemoBoard = board.id == DEMO_BOARD_SLUG;
  const quoteIsViewOnly = boardHasBeenPaidFor(board) || isDemoBoard;
  const isProcessingQuote =
    useAppSelector(
      (state) =>
        (state.uploadedBoard.boards[board.id] as BoardContext).isProcessing,
    ) && board.quote.state != QuoteState.ORDER_RECEIVED;
  const onChangeLeadTimeDays = (
    newValue: SingleValue<LeadTimeSelectOption>,
  ) => {
    if (newValue) {
      updateShipDate(
        shipments[0]!.id,
        // Changing the lead time from this perspective, we deliberately only send the base days
        // because we want the ship date to move with adjustments
        newValue.value.ship_date,
        newValue.value.base_days,
      );
    }
  };

  const leadTimeSelectOptions = shipments[0]!.lead_time_options.options.map(
    (leadTimeOption) => {
      return {
        value: leadTimeOption,
        label: leadTimeInfoString(
          leadTimeOption.days,
          leadTimeOption.ship_date,
          leadTimeOption.disabled,
        ),
        disabled: leadTimeOption.disabled,
      };
    },
  );

  const leadTimeValue = useGetLeadTimeValue();

  const getFirstLeadTimeShipDate = () => {
    if (firstShipment.lead_time_days) {
      return leadTimeInfoString(
        firstShipment.lead_time_days,
        firstShipment.ship_date,
      );
    } else if (firstShipment.ship_date) {
      return dateStringToHumanReadableDate(firstShipment.ship_date, true);
    }
  };

  const boardHasMultipleShipments = shipments.length > 1;
  return (
    <>
      <LoadingOverlay
        loading={updatingShipDate}
        text={"Updating lead time..."}
      />
      {(quoteIsViewOnly || boardHasMultipleShipments) && (
        <>
          <LineItem
            leftText={
              boardHasBeenPaidFor(board)
                ? "Number of boards quoted for order"
                : "Number of boards"
            }
            rightText={board.quote.num_boards.toLocaleString()}
            isUpdating={isProcessingQuote}
          />
          <LineItem
            leftText={
              boardHasMultipleShipments ? "Number of shipments" : "Ship date"
            }
            rightText={
              boardHasMultipleShipments
                ? shipments.length
                : getFirstLeadTimeShipDate()
            }
          />
        </>
      )}

      {!quoteIsViewOnly && !boardHasMultipleShipments && (
        <div>
          <Label>Number of boards:</Label>
          <NumBoardsContainer>
            {isProcessingQuote ? <ProcessingPill /> : board.quote.num_boards}
            <EditButton
              onClick={
                updatingShipDate || updatingShipment || isProcessingQuote
                  ? undefined
                  : () => setEditingNumBoards(true)
              }
              disabled={
                updatingShipDate || updatingShipment || isProcessingQuote
              }
            >
              <Icon src="/icons/pencil.svg" alt="edit" />
            </EditButton>
          </NumBoardsContainer>
          <DetailLine>
            <QuoteOptionContainer>
              <Label>Lead time:</Label>
              <FlashPCBSelect
                options={leadTimeSelectOptions}
                value={leadTimeValue()}
                onChange={onChangeLeadTimeDays}
                isOptionDisabled={(option: LeadTimeSelectOption) =>
                  option.disabled
                }
              />
              <LineItemDescription>
                <a
                  href={"/capabilities#lead-time"}
                  target="_blank"
                  rel="noreferrer"
                >
                  See more information
                </a>
              </LineItemDescription>
            </QuoteOptionContainer>
          </DetailLine>
        </div>
      )}
      {editingNumBoards && shipments.length === 1 && (
        <Modal
          isOpen={editingNumBoards && shipments.length === 1}
          onRequestClose={() => setEditingNumBoards(false)}
        >
          <ModalContentWithCloseButton
            closeModal={() => setEditingNumBoards(false)}
            title={"Edit shipment"}
          >
            <AddOrEditShipment
              onUpdate={() => setEditingNumBoards(false)}
              shipmentId={shipments[0]!.id}
            />
          </ModalContentWithCloseButton>
        </Modal>
      )}

      {shipments[0]!.requires_stencil && (
        <LineItem
          leftText="X-ray inspection"
          rightText={
            <Checkbox
              checked={
                board.manufacturing_details.customer_specifications
                  .xray_inspection
              }
              disabled={quoteIsViewOnly}
              onChange={() => {
                updateXrayInspection(
                  !board.manufacturing_details.customer_specifications
                    .xray_inspection,
                );
              }}
            />
          }
        />
      )}
      {shipments[0]!.shipment_parts.length > 0 && (
        <LineItem
          leftText="Cleaning"
          rightText={
            <Checkbox
              checked={
                board.manufacturing_details.customer_specifications.cleaning
              }
              disabled={quoteIsViewOnly}
              onChange={() => {
                updateCleaning(
                  !board.manufacturing_details.customer_specifications.cleaning,
                );
              }}
            />
          }
          description={
            <a href={"/blog/PCB-Cleaning"} target="_blank" rel="noreferrer">
              Learn more
            </a>
          }
        />
      )}
      <LineItem
        leftText="Conformal coating"
        rightText={
          <Checkbox
            checked={
              board.manufacturing_details.customer_specifications
                .conformal_coating
            }
            disabled={quoteIsViewOnly}
            onChange={() => {
              updateConformalCoating(
                !board.manufacturing_details.customer_specifications
                  .conformal_coating,
              );
            }}
          />
        }
      />
      <LineItem
        leftText={
          <ToolTipWrapper>
            {"Include date code"}
            <FlashPCBTooltip
              content={
                "Please select this if your board design contains a silkscreen date code that you would like us to print"
              }
            >
              <InfoIcon />
            </FlashPCBTooltip>
          </ToolTipWrapper>
        }
        rightText={
          <Checkbox
            checked={
              board.manufacturing_details.customer_specifications
                .include_date_code
            }
            disabled={quoteIsViewOnly}
            onChange={() => {
              updateIncludeDateCode(
                !board.manufacturing_details.customer_specifications
                  .include_date_code,
              );
            }}
          />
        }
      />
      <DetailLine>
        <QuoteOptionContainer>
          <Label>Special requirements / notes:</Label>
          <TextArea
            minHeight={"6rem"}
            placeholder={"Please describe your special requirements here."}
            disabled={quoteIsViewOnly}
            value={board.manufacturing_details.customer_specifications.notes}
            onChange={(event) => updateNotes(event.target.value)}
          />
          <LineItem
            description={
              <a href={"/capabilities#specs"} target="_blank" rel="noreferrer">
                See capabilities
              </a>
            }
          />
        </QuoteOptionContainer>
      </DetailLine>
    </>
  );
}
